import { useMemo } from "react";
import { B, HD, H, PD, SC } from "../theme";
import { StarField } from "../shared/StarField";
import { BackArrow } from "../shared/BackArrow";
import { Constellation } from "../shared/Constellation";
import { GhostButton, SKY } from "../shared/ui";
import { computeNatal } from "../lib/astro/natal";
import { birthLine } from "../onboarding/Reveal";
import { traditionTiles } from "../onboarding/traditionTiles";
import { calculateVedicChart } from "../components/star-charts/vedicMath";
import { calculateChineseChart } from "../lib/astro/chinese";
import { calculateMayanChart } from "../lib/astro/mayan";
import { WesternGlyph, VedicGlyph } from "../components/star-charts/ZodiacGlyph";
import { ChineseGlyph } from "../components/star-charts/ChineseGlyph";
import { MayanGlyph } from "../components/star-charts/MayanGlyph";
import { YourDays } from "./YourDays";

/* The four traditions side by side: equal nodes on one ring, like the
   original Star Charts sub-constellation. None of them is the default; each
   opens its own full screen. It opens from the Your stars rows on Today and
   the Web. */

export const SYSTEMS = [
  { key: "western", label: "Western", accent: "#68B8C8", x: 26, y: 24 },
  { key: "vedic", label: "Vedic", accent: "#D4A574", x: 74, y: 24 },
  { key: "mayan", label: "Maya", accent: "#6DA88F", x: 26, y: 80 },
  { key: "chinese", label: "Chinese", accent: "#C2625A", x: 74, y: 80 },
];

const GLYPH = 76;
// Node centers sit in the ring's 100 x 120 box (extra height so two-line
// values clear the node below, down to 320px wide).
const BOX_H = 120;

const at = (bd, hour, minute, period) => ({ ...bd, hasBirthTime: true, birthTime: { hour, minute, period } });

export function nodeFacts(birthData) {
  const facts = {};
  const tiles = Object.fromEntries(traditionTiles(birthData).map((t) => [t.key, t]));
  try {
    const natal = computeNatal(birthData);
    const r = natal.sunRange;
    // A sign badge only when the sign is certain; "X or Y" gets a plain node.
    facts.western = { value: r ? `${r.from} or ${r.to}` : natal.points.sun.sign, sub: "Sun sign", glyph: r ? null : natal.points.sun.signKey };
  } catch (e) { /* unavailable for this date */ }
  try {
    // Untimed: the Moon can change sidereal sign during the day (same samples
    // as the tile), so show its sign only when the whole day agrees.
    const moonOf = (bd) => calculateVedicChart(bd)?.planets?.moon;
    const moonSign = (bd) => moonOf(bd)?.sign;
    const signs = birthData.hasBirthTime
      ? [moonSign(birthData)]
      : [...new Set([moonSign(at(birthData, 12, 0, "AM")), moonSign(at(birthData, 12, 0, "PM")), moonSign(at(birthData, 11, 59, "PM"))])];
    const moon = signs.length === 1 ? signs[0] : null;
    // Name the badge's sign, by its Sanskrit name first, so the sidereal Moon
    // doesn't read as a second Sun sign or a wrong Western Moon.
    const sanskrit = moon && moonOf(birthData.hasBirthTime ? birthData : at(birthData, 12, 0, "PM"))?.signSanskrit;
    const sub = moon ? `Moon mansion in ${sanskrit ? `${sanskrit} (${moon})` : moon}` : tiles.vedic.sub;
    if (tiles.vedic) facts.vedic = { value: tiles.vedic.value, sub, glyph: moon && String(moon).toLowerCase() };
  } catch (e) { /* unavailable */ }
  try {
    const c = calculateChineseChart(birthData);
    if (tiles.chinese) facts.chinese = { value: tiles.chinese.value, sub: tiles.chinese.sub, glyph: c.animal.char };
  } catch (e) { /* unavailable */ }
  try {
    const m = calculateMayanChart(birthData);
    if (tiles.mayan) facts.mayan = { value: tiles.mayan.value, sub: `Day sign · ${tiles.mayan.sub}`, glyph: m.daySign.key };
  } catch (e) { /* unavailable */ }
  return facts;
}

export function NodeGlyph({ k, glyph, outline, accent, size = GLYPH }) {
  if (!glyph) {
    return (
      <svg viewBox="0 0 76 76" width={size} height={size} aria-hidden style={{ display: "block" }}>
        <circle cx="38" cy="38" r="30" fill={accent} fillOpacity="0.1" stroke={accent} strokeWidth="1.6" opacity={outline ? 0.55 : 0.9} />
        <circle cx="38" cy="38" r="4" fill={accent} opacity="0.7" />
      </svg>
    );
  }
  if (k === "western") return <WesternGlyph sign={glyph} size={size} outline={outline} />;
  if (k === "vedic") return <VedicGlyph sign={glyph} size={size} outline={outline} />;
  if (k === "chinese") return <ChineseGlyph char={glyph} size={size} outline={outline} />;
  return <MayanGlyph daySign={glyph} size={size} outline={outline} />;
}

export function ChartMenu({ birthData, viewed = {}, onOpenTradition, onEditBirth, onOpenSettings, onBack }) {
  const facts = useMemo(() => nodeFacts(birthData), [birthData]);
  const systems = SYSTEMS.filter((s) => facts[s.key]);

  return (
    <div style={{ minHeight: "100vh", backgroundColor: PD.bg, position: "relative", overflow: "hidden",
      backgroundImage: `radial-gradient(ellipse at 30% 20%, ${SC.accent}12 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, #1A284040 0%, transparent 50%)` }}>
      <StarField seed={birthData.birthDate.day * 31 + birthData.birthDate.month} />
      <Constellation seed={777} threads={6} accent={SC.accent} luminous={true} style={{ opacity: 0.4 }} />
      <div style={{ position: "relative", zIndex: 1, maxWidth: 480, margin: "0 auto", padding: "28px 20px 40px", animation: "twFade 0.5s ease both" }}>
        <header style={{ marginBottom: 8 }}>
          {onBack && <div style={{ marginTop: -14 }}><BackArrow onClick={onBack} /></div>}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <p style={{ ...H, fontSize: 18, fontStyle: "italic", color: PD.text, minWidth: 0 }}>{birthLine(birthData)}</p>
            {onOpenSettings && (
              <button type="button" onClick={onOpenSettings} aria-label="Settings" style={{ background: "none", border: "none", padding: 10, margin: -10, cursor: "pointer", minWidth: 44, minHeight: 44 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <circle cx="12" cy="12" r="3" stroke={PD.sub} strokeWidth="1.5" />
                  <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1" stroke={PD.sub} strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
          <GhostButton onClick={onEditBirth} style={{ padding: "8px 0", fontSize: 13, color: SKY.b }}>Edit birth details</GhostButton>
        </header>

        <div style={{ textAlign: "center", marginTop: 22 }}>
          <h1 style={{ ...HD, fontSize: "clamp(22px, 6vw, 30px)", color: PD.text, margin: 0, fontWeight: 500 }}>Choose your system</h1>
        </div>

        <div style={{ position: "relative", width: "100%", maxWidth: 360, aspectRatio: `100 / ${BOX_H}`, margin: "18px auto 0" }}>
          <svg viewBox={`0 0 100 ${BOX_H}`} preserveAspectRatio="none" aria-hidden style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            <circle cx="50" cy="52" r="36.9" stroke={PD.thread} strokeWidth="0.3" opacity="0.5" fill="none" vectorEffect="non-scaling-stroke" />
            {systems.map((s) => (
              <line key={s.key} x1="50" y1="52" x2={s.x} y2={s.y} stroke={s.accent} strokeWidth="0.6" opacity="0.3" vectorEffect="non-scaling-stroke" />
            ))}
            <circle cx="50" cy="52" r="1.6" fill={SC.accent} opacity="0.45" />
          </svg>
          {systems.map((s, i) => {
            const f = facts[s.key];
            const fresh = !viewed[s.key];
            return (
              <button key={s.key} type="button" onClick={() => onOpenTradition(s.key)} className="tw-card-tap"
                aria-label={`${s.label}: ${f.value}, ${f.sub}${fresh ? ", not opened yet" : ""}`}
                style={{
                  position: "absolute", left: `${s.x}%`, top: `${(s.y / BOX_H) * 100}%`, transform: `translate(-50%, -${4 + GLYPH / 2}px)`,
                  width: "46%", display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                  background: "none", border: "none", padding: 4, cursor: "pointer", color: PD.text,
                  animation: `twFade 0.5s ease ${0.08 * i}s both`,
                }}>
                <span style={{ position: "relative", width: GLYPH, height: GLYPH, display: "block" }}>
                  <span aria-hidden style={{ position: "absolute", inset: -10, borderRadius: "50%", background: `radial-gradient(circle, ${s.accent}22 0%, transparent 70%)` }} />
                  <span style={{ position: "relative", display: "block" }}><NodeGlyph k={s.key} glyph={f.glyph} outline={fresh} accent={s.accent} /></span>
                  {/* Green dot = not opened yet, "come look". */}
                  {fresh && <span aria-hidden style={{ position: "absolute", top: 2, right: 2, width: 11, height: 11, borderRadius: 6, background: "#5E8A6A" }} />}
                </span>
                <span style={{ ...B, fontSize: 13, fontWeight: 500, color: s.accent, marginTop: 6 }}>{s.label}</span>
                <span style={{ ...B, fontSize: f.value.length > 16 ? 13 : 15, fontWeight: 600, lineHeight: 1.3, textAlign: "center", overflowWrap: "anywhere" }}>{f.value}</span>
                <span style={{ ...B, fontSize: 12, color: PD.sub }}>{f.sub}</span>
              </button>
            );
          })}
        </div>

        <div style={{ textAlign: "center", marginTop: 12 }}>
          <GhostButton onClick={() => onOpenTradition("sky")} style={{ color: SKY.b, fontSize: 14 }}>See the real sky you were born under{"\u00A0"}→</GhostButton>
        </div>

        <YourDays birthData={birthData} onEditBirth={onEditBirth} />
      </div>
    </div>
  );
}
